import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap, ShieldCheck, CreditCard, Check } from 'lucide-react';
import { trackInitiateCheckout } from '../lib/pixel';
type Selection = 'standard' | 'elite';
const CHECKOUT_URLS: Record<Selection, string> = {
  standard: 'https://profelisto.com.ar/checkouts/cn/hWNCOaSJE7otUOoTtN35qv6Q/es-ar?_r=AQABWd7vJL-OjcTRtS-AK8ijDDJ4ZGabub_-Vdhjn4tuR-c',
  elite: 'https://profelisto.com.ar/checkouts/cn/hWNCOaUzod0ZMOWRh0mUK5bu/es-ar?_r=AQABcwAg0WKh_x_0uzXShYV5MUl-G4cgn1N2t2DXeOjlKL0'
};
const CHECKOUT_VALUES: Record<Selection, {
  value: number;
  name: string;
}> = {
  standard: {
    value: 18990,
    name: 'Portugués 360 — Pack Completo'
  },
  elite: {
    value: 27990,
    name: 'Portugués 360 Elite'
  }
};
export function PricingCards() {
  const [selected, setSelected] = useState<Selection>('elite');
  const goToCheckout = () => {
    trackInitiateCheckout({
      value: CHECKOUT_VALUES[selected].value,
      currency: 'ARS',
      content_name: CHECKOUT_VALUES[selected].name
    });
    window.location.href = CHECKOUT_URLS[selected];
  };
  return <section id="pricing" className="w-full bg-deep-green py-20 px-4 scroll-mt-10" data-id="element-234">
      <div className="max-w-2xl mx-auto" data-id="element-235">
        <div className="text-center mb-12" data-id="element-236">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight" data-id="element-237">
            Elegí tu <span className="text-gold" data-id="element-238">Portugués 360</span>:
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-base md:text-lg text-green-100/80" data-id="element-239">
            Las dos opciones incluyen los 6 bonus estratégicos
          </motion.p>
        </div>

        {/* Stacked Option Cards */}
        <div className="space-y-4 mb-8" data-id="element-240">
          {/* CARD 1 — STANDARD */}
          <motion.button type="button" onClick={() => setSelected('standard')} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} whileHover={{
          scale: 1.005
        }} className={`w-full text-left bg-black/30 rounded-2xl p-5 md:p-6 border-2 transition-all ${selected === 'standard' ? 'border-gold shadow-[0_0_0_4px_rgba(245,158,11,0.15)]' : 'border-white/10 hover:border-white/30'}`} aria-pressed={selected === 'standard'} data-id="element-241">
            <div className="flex items-center gap-4" data-id="element-242">
              {/* Radio */}
              <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selected === 'standard' ? 'border-gold bg-gold' : 'border-white/40'}`} data-id="element-243">
                {selected === 'standard' && <div className="w-2.5 h-2.5 rounded-full bg-deep-green" data-id="element-244" />}
              </div>

              {/* Content */}
              <div className="flex-grow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-245">
                <div data-id="element-246">
                  <div className="flex items-center gap-2 mb-1" data-id="element-247">
                    <span className="text-xl" data-id="element-248">📘</span>
                    <h3 className="text-base md:text-lg font-extrabold text-white tracking-tight" data-id="element-249">
                      PORTUGUÉS 360
                    </h3>
                  </div>
                  <p className="text-sm text-green-100/70" data-id="element-250">
                    12 Tomos + 6 Bonus — Todos los niveles
                  </p>
                </div>
                <div className="text-right sm:text-right flex sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0" data-id="element-251">
                  <span className="text-gray-400 line-through text-sm font-medium" data-id="element-252">
                    $60.000
                  </span>
                  <span className="text-white font-extrabold text-3xl md:text-4xl leading-none" data-id="element-253">
                    $18.990
                  </span>
                </div>
              </div>
            </div>
          </motion.button>

          {/* CARD 2 — ELITE */}
          <motion.button type="button" onClick={() => setSelected('elite')} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} whileHover={{
          scale: 1.005
        }} className={`w-full text-left bg-black/30 rounded-2xl p-5 md:p-6 border-2 transition-all relative ${selected === 'elite' ? 'border-gold shadow-[0_0_0_4px_rgba(245,158,11,0.15)]' : 'border-white/10 hover:border-white/30'}`} aria-pressed={selected === 'elite'} data-id="element-254">
            <div className="flex items-center gap-4" data-id="element-255">
              {/* Radio */}
              <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selected === 'elite' ? 'border-gold bg-gold' : 'border-white/40'}`} data-id="element-256">
                {selected === 'elite' && <div className="w-2.5 h-2.5 rounded-full bg-deep-green" data-id="element-257" />}
              </div>

              {/* Content */}
              <div className="flex-grow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-258">
                <div className="flex-grow" data-id="element-259">
                  <div className="flex items-center gap-2 mb-2" data-id="element-260">
                    <span className="text-xl" data-id="element-261">🏆</span>
                    <h3 className="text-base md:text-lg font-extrabold text-white tracking-tight" data-id="element-262">
                      PORTUGUÉS 360 ELITE
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-263">
                    <span className="inline-flex items-center gap-1 bg-gold text-deep-green text-[10px] md:text-xs font-extrabold px-2.5 py-1 rounded-full" data-id="element-264">
                      ⭐ MÁS ELEGIDO
                    </span>
                    <span className="inline-flex items-center bg-green-700/40 border border-green-400/40 text-green-200 text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-265">
                      AHORRÁS $41.010
                    </span>
                  </div>
                  <p className="text-sm text-green-100/70 leading-snug" data-id="element-266">
                    12 Tomos + 6 Bonus + 2 Ebooks Elite (Método Viajero &
                    Corrección Pro)
                  </p>
                </div>
                <div className="text-right flex sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0 flex-shrink-0" data-id="element-267">
                  <span className="text-gray-400 line-through text-sm font-medium" data-id="element-268">
                    $70.000
                  </span>
                  <span className="text-gold font-extrabold text-3xl md:text-4xl leading-none" data-id="element-269">
                    $27.990
                  </span>
                </div>
              </div>
            </div>
          </motion.button>
        </div>

        {/* Single CTA */}
        <motion.button onClick={goToCheckout} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.99
      }} className="w-full bg-gold hover:bg-yellow-400 text-deep-green font-extrabold text-base md:text-xl py-5 px-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all" data-id="element-270">
          →{' '}
          {selected === 'elite' ? 'SÍ, QUIERO EL PACK ELITE' : 'QUIERO MIS 12 TOMOS + 6 BONUS'}
        </motion.button>

        <p className="text-center italic text-sm text-green-100/60 mt-5 mb-8" data-id="element-271">
          {selected === 'elite' ? 'La opción más elegida por docentes que quieren facturar más y trabajar menos.' : 'La opción ideal para empezar a resolver tu año completo desde hoy.'}
        </p>

        {/* Trust badges row */}
        <div className="bg-black/20 border border-green-400/20 rounded-xl px-4 py-3" data-id="element-272">
          <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-xs md:text-sm text-green-100/80 font-medium" data-id="element-273">
            <div className="flex items-center gap-1.5" data-id="element-274">
              <Lock size={14} data-id="element-275" />
              <span data-id="element-276">Pago seguro</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-277">
              <Zap size={14} className="text-yellow-400" data-id="element-278" />
              <span data-id="element-279">Acceso en 5 min</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-280">
              <ShieldCheck size={14} data-id="element-281" />
              <span data-id="element-282">Garantía 7 días</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-283">
              <CreditCard size={14} data-id="element-284" />
              <span data-id="element-285">Mercado Pago</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}